/**
 * Created by kevin on 2016-10-20.
 *
 * Implements a filter sidebar component as an accessory to a file/search list
 *
 */

import { Component, OnChanges, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseDesc } from '../models/base';
import { ListItem } from '../models/list-item';
import { Util } from '../utils/utils.module';
import { ShortcutsService, KeyCommand } from '../services/shortcuts.service';
import { LocalizeService } from '../services/localize.service';


@Component({
  selector: 'edx-filelist-sidebar',
  styleUrls: ['filelist-sidebar.component.scss'],
  template: `
    <div class="filelistcontainer" role="list" [ngClass]="{disabled:disabled, adminformconfig: isAdminFormConfiguration()}">
      @for (listitem of list; track listitem; let i = $index) {
        <div role="listitem" title="{{RestAPI.itemToolTipText(listitem)}}" [ngClass]="{ fileitem: true, selected: (selectIndex===i), disableHover:disabled }" [tabindex]="disabled ? -1 : tabIndex" (click)="selectFile(i)" [id]="getElementId(i)" (keyup)="onKeyUp($event,i)">
          <img class="fileicon" src="{{icons[i]}}" alt="{{altTexts[i]}}">
          @if (displayMiniStatus(listitem)) {
            <img class="overlaystatusicon" src="{{formatStatusIcon(listitem)}}" alt="{{formatStatusText(listitem)}}">
          }
          @if (displaySecondMiniStatus(listitem)) {
            <img class="overlaystatusicon top right clickable" src="{{formatSecondStatusIcon(listitem)}}" alt="{{formatStatusText(listitem)}}" (click)="listItemClick(listitem, $event, 'APP_ID')" [title]="isAdminLookupItemSecured(listitem) ? secured : null">
          }
          @if (displayTopMiniStatus(listitem)) {
            <img class="overlaystatusicon top clickable" src="{{formatTopStatusIcon(listitem)}}" alt="{{formatStatusText(listitem)}}" (click)="listItemClick(listitem, $event, 'APP_ID')" [title]="isAdminLookupItemSecured(listitem) ? secured : null">
          }
          <div class="filename">{{listitem.DOCNAME}}</div>
          @if ((selectIndex===i)) {
            <div class="selectarrow"></div>
          }
        </div>
      }
    </div>
    `,
  standalone: false
})
export class FilelistSidebarComponent implements OnChanges, OnDestroy {
  @Input() receiver: FilelistReceiver;
  @Input() list: ListItem[] = [];
  @Input() selectIndex?: number = 0;
  @Input() rerender = 0;
  @Input() desc: BaseDesc;
  @Input() disabled?: boolean = false;
  @Input() tabIndex?: number = 0;
  @Input() schemaId?: string = null;
  private icons: string[] = [];
  private altTexts: string[] = [];
  private shortcutsSubscription: Subscription;
  private secured: string = '';
  RestAPI = Util.RestAPI;

  constructor(private shortcutsService: ShortcutsService, private localizer: LocalizeService) {
    this.shortcutsSubscription = shortcutsService.commands.subscribe(c => this.handleCommand(c));
    this.secured = this.localizer.getTranslation('COLUMN_HEADINGS.DEFAULT.SECURE');
  }

  handleCommand(command: KeyCommand) {
    if (this.isAdminFormConfiguration()) {
      return;
    }
    switch (command.name) {
      case 'file-list-select-next':
        this.navigateToFile(this.selectIndex + 1);
        break;
      case 'file-list-select-prev':
        this.navigateToFile(this.selectIndex - 1);
        break;
    }
  }

  ngOnDestroy() {
    if (this.shortcutsSubscription) {
      this.shortcutsSubscription.unsubscribe();
    }
  }

  ngOnChanges() {
    this.getIcons();
  }

  private getIcons(): void {
    if (this.list) {
      this.icons = [];
      this.altTexts = [];
      for (const item of this.list) {
        this.icons.push(Util.Transforms.iconUrlFromDesc(item));
        this.altTexts.push(Util.Transforms.iconAltTextFromDesc(item));
      }
    }
  }

  private selectFile(index: number): void {
    if (!this.disabled) {
      this.selectIndex = index;
      this.receiver.fileSelected(index);
    }
  }

  private displayMiniStatus(item: ListItem): boolean {
    return Util.RestAPI.displayMiniStatus(item, this.desc);
  }

  private displayTopMiniStatus(item: ListItem): boolean {
    return Util.RestAPI.displayTopMiniStatus(item, this.desc) || (this.isAdminLookupItemSecured(item) && Util.RestAPI.getLibraryEffectiveRights(this.desc.lib)?.['LOOKUP_SECURITY'] === 'Y');
  }

  private displaySecondMiniStatus(item: ListItem): boolean {
    return Util.RestAPI.displaySecondMiniStatus(item, null);
  }

  private isAdminLookupItemSecured(item: ListItem): boolean {
    return this.desc.id === 'admin' && item?.type === 'lookups' && item?.secured;
  }

  private formatStatusIcon(item: ListItem): string {
    return Util.RestAPI.formatStatusIcon(item, this.desc);
  }

  private formatTopStatusIcon(item: ListItem): string {
    return Util.RestAPI.formatTopStatusIcon(item, this.desc);
  }

  private formatSecondStatusIcon(item: ListItem): string {
    return Util.RestAPI.formatSecondStatusIcon(item, null);
  }

  private formatStatusText(item: ListItem): string {
    return Util.RestAPI.formatStatusText(item);
  }

  public disable(disable: boolean): void {
    this.disabled = disable;
  }

  private getElementId(index: number): string {
    return 'edx_sidebar_item_' + (this.isAdminFormConfiguration() ? 'app_' : '') + index;
  }

  private onKeyUp(event: KeyboardEvent, index: number): void {
    let elementId: string = null;
    switch (event.key) {
      case 'ArrowDown':
        elementId = this.getElementId(++index);
        break;
      case 'ArrowUp':
        elementId = this.getElementId(--index);
        break;
      case 'ArrowRight':
        elementId = 'edx_header_menu';
        break;
      case 'ArrowLeft':
        elementId = 'edx_sidebar_item_' + this.selectIndex;
        break;
      case 'Enter':
        this.selectFile(index);
        if (this.isAdminFormConfiguration()) {
          event.stopPropagation();
          event.preventDefault();
        }
        break;
      case ' ':
        this.selectFile(index);
        break;
    }
    if (!!elementId && !Util.Transforms.focusOnElementById(elementId)) {
      if (event.key === 'ArrowRight' && !Util.Transforms.focusOnElementByQuerySelector('#edx_action_bar .actionitem:not(.hidden)')) {
        if (this.desc?.id === 'preferences' || this.desc?.id === 'admin' || this.isAdminFormConfiguration()) {
          this.receiver?.focusOnSpecificElement();
        }
      }
    }
  }

  private navigateToFile(index: number): void {
    const element: HTMLElement = document.getElementById(this.getElementId(index));
    if (!!element) {
      this.selectFile(index);
      element.focus();
    }
  }

  public isAdminFormConfiguration(): boolean {
    return this.schemaId === 'GROUP_APP_ID_FORMS';
  }

  public focusOnFirstItem(): void {
    Util.Transforms.focusOnElementById(this.getElementId(this.selectIndex));
  }
}

export interface FilelistReceiver {
  fileSelected(index: number): void;
  focusOnSpecificElement?(): void;
}
